import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsConditions from './pages/TermsConditions';
import EventsConditions from './pages/EventsConditions';
import InformativaSitoWeb from './pages/InformativaSitoWeb';
import InformativaGenerale from './pages/InformativaGenerale';
import CourseQuizCampaigns from './pages/CourseQuizCampaigns';
import CourseQuizCampaignEditor from './pages/CourseQuizCampaignEditor';
import { getMessaging, getToken, isSupported, onMessage } from 'firebase/messaging';
import { useEffect, useRef, useState, useCallback } from 'react';
import { Navigate, useLocation, Route, BrowserRouter as Router, Routes } from "react-router-dom";
import "./App.css";
import api from './api';
import ErrorBoundary from './common/ErrorBoundary';
import MainContext from "./common/MainContext";
import { Role, UserPages, SHOW_NOTIFICATIONS } from './common/constants';
import useWindowDimensions from './common/hooks/useWindowDimensions';
import "./common/i18n";
import ErrorBanner from './components/ErrorBanner';
import NotificationBanner from './components/NotificationBanner';
import ProtectedRoute from './components/ProtectedRoute';
import LoginDialog from './components/dialogs/LoginDialog';
import NotificationPermissionDialog from './components/dialogs/NotificationPermissionDialog';
import SideNav from './components/layouts/SideNav';
import ThemeProvider from "./components/layouts/ThemeProvider";
import MacroEditor from './components/macros/MacroEditor';
import MacroPreview from './components/macros/MacroPreview';
import CoursePreview from './components/previews/CoursePreview';
import app, { auth } from "./firebase";
import ActionHandler from './pages/ActionHandler';
import Admin from './pages/Admin';
import Applications from './pages/Applications';
import ApplicationsV2 from './pages/ApplicationsV2';
import ArticleEditor from './pages/ArticleEditor';
import Articles from './pages/Articles';
import Career from './pages/Career';
import Careers from './pages/Careers';
import ChangePassword from './pages/ChangePassword';
import Company from './pages/Company';
import CompanyUser from './pages/CompanyUser';
import Communications from './pages/Communications';
import Coupons from './pages/Coupons';
import Course from './pages/Course';
import CourseEditor from "./pages/CourseEditor";
import CourseReports from './pages/CourseReports';
import Courses from './pages/Courses';
import Dashboard from "./pages/Dashboard";
import CompanyCourseDashboard from './pages/dashboard/CompanyCourseDashboard';
import Developers from './pages/Developers';
import DevelopersDocumentation from './pages/DevelopersDocumentation';
import DeveloperKeyDetails from './pages/DeveloperKeyDetails';
import EditionEditor from './pages/EditionEditor';
import Exercise from './pages/Exercise';
import Exercises from './pages/Exercises';
import Feedbacks from './pages/Feedbacks';
import InvoiceRequests from './pages/InvoiceRequests';
import LiveLesson from './pages/LiveLesson';
import Login from './pages/Login';
import Macros from './pages/Macros';
import Forms from './pages/Forms';
import Mailings from './pages/Mailings';
import MarketingReports from './pages/MarketingReports';
import Materials from './pages/Materials';
import ModuleEditor from './pages/ModuleEditor';
import Notifications from "./pages/Notifications";
import Order from './pages/Order';
import Orders from './pages/Orders';
import OrdersReports from './pages/OrdersReports';
import PageNotFound from './pages/PageNotFound';
import Profile from './pages/Profile';
import Reports from './pages/Reports';
import ReviewEditor from './pages/ReviewEditor';
import Reviews from './pages/Reviews';
import Settings from './pages/Settings';
import ShopCourses from './pages/ShopCourses';
import Student from './pages/Student';
import StudentSurvey from './pages/StudentSurvey';
import Survey from './pages/Survey';
import Surveys from './pages/Surveys';
import Teacher from './pages/Teacher';
import TeachingReports from './pages/TeachingReports';
import TestResult from './pages/TestResult';
import Tests from './pages/Tests';
import Users from './pages/Users';
import Video from './pages/Video';
import VideoEditor from './pages/VideoEditor';
import Videos from './pages/Videos';
import Downloads from './pages/Downloads';
import DownloadEditor from './pages/DownloadEditor';
import RecordingsEditor from './pages/RecordingsEditor';
import "./themes.css";
import SurveyEditor from './pages/SurveyEditor';
import Lessons from './pages/Lessons';
import Chats from './pages/Chats';
import Events from './pages/Events';
import { WebSocketProvider } from './common/WebSocketContext';
import { mapServerError } from './common/Errors';
import EventEditor from './pages/EventEditor';
import Event from './pages/Event';
import EventManager from './pages/EventManager';
import EventPreview from './components/previews/EventPreview';
import ArticlePreview from './components/previews/ArticlePreview';
import RecordingsJobs from './pages/RecordingsJobs';

function LegacyCampaignRedirect() {
    const location = useLocation();
    return <Navigate replace to={`${location.pathname.replace(/^\/course-quiz-campaigns/, '/campaigns')}${location.search}${location.hash}`} />;
}

function App() {
  const { width } = useWindowDimensions()
  const [user, setUser] = useState(null)
  const [dropdown, setDropdown] = useState(null)
  const [loginDialog, setLoginDialog] = useState(null)
  const [sidenav, setSideNav] = useState(true)
  const [notificationPermissions, setNotificationPermissions] = useState(false)
  const [notificationBanner, setNotificationBanner] = useState(null)
  const [notifications, setNotifications] = useState(null)
  const [unreadMessages, _setUnreadMessages] = useState(0)
  const [whiteboard, setWhiteboard] = useState({ show: false, persistenceKey: null, pdf: null })
  const [error, setError] = useState(null)
  const [sideNavOpen, setSideNavOpen] = useState(width >= 768)
  const [authReady, setAuthReady] = useState(false);
  const [fcmToken, setFcmToken] = useState(null)
  const scrollRef = useRef(null)

  const setUnreadMessages = data => {
    unreadMessageRef.current = data
    _setUnreadMessages(data)
  }

  const context = {
    user: user,
    setUser: setUser,
    isLogging: null,
    dropdown: dropdown,
    setDropdown: setDropdown,
    loginDialog: loginDialog,
    setLoginDialog: setLoginDialog,
    sidenav: sidenav,
    setSideNav: setSideNav,
    notificationPermissions: notificationPermissions,
    setNotificationPermissions: setNotificationPermissions,
    notificationBanner: notificationBanner,
    setNotificationBanner: setNotificationBanner,
    notifications: notifications,
    setNotifications: setNotifications,
    unreadMessages: unreadMessages,
    setUnreadMessages: setUnreadMessages,
    whiteboard: whiteboard,
    setWhiteboard: setWhiteboard,
    scrollRef,
    error: error,
    setError: setError,
    sideNavOpen: sideNavOpen,
    setSideNavOpen: setSideNavOpen,
    fcmToken: fcmToken,
    setFcmToken: setFcmToken,
  }

  const unreadMessageRef = useRef(context.unreadMessages)
  const getUserInFlight = useRef(false)
  const serverWaitBannerShown = useRef(false)

  const getNotifications = useCallback(async () => {
    // try {
    //   let notifications = await api.get("/admin/notifications?page=1&per=4")
    //   setNotifications(notifications)
    // }
    // catch (e) {
    //   console.error(e)
    // }
  }, [])

  //Ottiene il token FCM e lo invia al backend.
  const getNotificationsToken = useCallback(async () => {
    try {
      let supported = await isSupported()
      if (supported) {
        await navigator.serviceWorker.register('/firebase-messaging-sw.js')
        let messaging = getMessaging(app)
        if (messaging) {
          if (Notification.permission === "granted") {
            try {
              let token = await getToken(messaging, { vapidKey: process.env.REACT_APP_FIREBASE_VAPID })
              setFcmToken(token)
              await api.post(user?.company_id ? "/company/tokens" : "/admin/tokens", { token: token })
            }
            catch (e) {
              console.error(e)
            }
          } else {
            console.warn("Notifications Permission Denied")
          }
        }
      }
    } catch (e) {
      console.error(e)
    }

  }, [user?.company_id])

  const handleError = useCallback((event) => {
    const error = mapServerError(event.detail);
    setError({ title: error.message, message: error.detail });
  }, [])

  useEffect(() => {

    if (window.location.hostname === "admin-beta.velv.academy") {
      const metaTag = document.createElement("meta");
      metaTag.name = "robots";
      metaTag.content = "noindex, nofollow";
      document.head.appendChild(metaTag);
    }

    //Gestisce le notifiche push ricevute con app aperta.
    isSupported().then(supported => {
      if (supported) {
        let messaging = getMessaging(app)
        if (messaging) {
          onMessage(messaging, (payload) => {
            setNotificationBanner({ title: payload.notification.title, body: payload.notification.body, image: payload.notification.image })
          })
        }
      }
    })


    document.addEventListener('api-error', handleError);
    return () => {
      document.removeEventListener('api-error', handleError)
    }

  }, [handleError]);

  // Inizializza lo stato di auth UNA sola volta
  useEffect(() => {
    let cancelled = false
    auth.authStateReady().then(() => {
      if (!cancelled) setAuthReady(true)
    })
    return () => {
      cancelled = true
    }
  }, [])

  const checkAuthUser = useCallback(async (user) => {
    //Se l'utente è loggato su firebase
    user = user ?? auth.currentUser

    if (user) {
      // //Ottiene le info dell'utente dal backend con retry.
      const getUser = async () => {
        if (getUserInFlight.current) {
          return
        }
        getUserInFlight.current = true
        const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms))
        const maxRetries = 5
        const delayMs = 2000

        for (let attempt = 1; attempt <= maxRetries; attempt++) {
          try {
            let u = await api.get("/admin/")
            if (u) {
              serverWaitBannerShown.current = false
              setNotificationBanner((prev) => prev?.type === 'server-wait' ? null : prev)
              setUser(u)
              setLoginDialog(false)
              getUserInFlight.current = false
              return
            }
          }
          catch (e) {
            console.error(`getUser attempt ${attempt} failed`, e)
            if (attempt < maxRetries) {
              await sleep(delayMs)
              continue
            }
            // Non forzare il logout su errori di raggiungibilita' del backend:
            // manteniamo la sessione e riproviamo in background.
            if (!serverWaitBannerShown.current) {
              setNotificationBanner({
                type: 'server-wait',
                title: 'In attesa della risposta dal server...',
                body: 'Riproviamo automaticamente senza disconnettere l\'utente.'
              })
              serverWaitBannerShown.current = true
            }
            console.warn('Unable to fetch /admin profile after retries. Keeping session active and retrying soon.')
            setTimeout(() => {
              getUserInFlight.current = false
              getUser()
            }, delayMs)
            return
          }
        }
        getUserInFlight.current = false
      }

      getUser()
    }
  }, [])

  // Quando auth è pronto, sottoscrivi i cambiamenti e affida a onAuthStateChanged la chiamata iniziale
  useEffect(() => {
    if (!authReady) return

    const unsubscribe = auth.onAuthStateChanged((user) => {
      checkAuthUser(user)
    })

    return () => unsubscribe()
  }, [authReady, checkAuthUser])

  /**
   * Quando cambia l'utente nel context
   */
  // Gestione side-effect dopo l'impostazione dell'utente
  useEffect(() => {
    if (user) {
      isSupported().then((supported) => {
        if (supported) {
          if (Notification.permission === 'default' && localStorage.getItem('notifications') !== 'denied') {
            setNotificationPermissions(true)
          }
          if (Notification.permission === 'granted') {
            localStorage.setItem('notifications', 'granted')
            if (!fcmToken) {
              getNotificationsToken()
            }
          }
        }
      })

      // //Elimina l'utente anonimo, ottieni il carrello, le notifiche e inizializza la connessione websocket per i messaggi
      if (notifications === null) {
        getNotifications()
      }

    }
  }, [user, fcmToken, notifications, getNotifications, getNotificationsToken])


  return (
    <Router>
      <MainContext.Provider value={context}>
        <ThemeProvider theme={'starting-finance'}>
          <div className="container">
            <WebSocketProvider user={context.user}>
              <ErrorBoundary>
                {!/^\/lessons\/[^/]+/.test(window.location.pathname) &&
                  <SideNav onChange={(open) => { context.setSideNavOpen(open) }}></SideNav>
                }
                <LoginDialog open={context.loginDialog} onClose={() => { context.setLoginDialog(false) }} />
                <NotificationPermissionDialog open={context.notificationPermissions} onClose={() => {
                  context.setNotificationPermissions(false)
                  getNotificationsToken()
                }} />
                <NotificationBanner notification={context.notificationBanner} />
                <ErrorBanner error={context.error} />

                <div className='content'>
                  <div ref={scrollRef} />
                  <Routes>
                    <Route path="/login" element={<Login />} />
                    <Route path="*" element={<PageNotFound />} />
                    <Route path="/actions" element={<ActionHandler />} />
                    <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                    <Route path="/terms-and-conditions" element={<TermsConditions />} />
                    <Route path="/events-conditions" element={<EventsConditions />} />
                    <Route path="/privacy-policy/informativa-sito-web" element={<InformativaSitoWeb />} />
                    <Route path="/privacy-policy/informativa-generale" element={<InformativaGenerale />} />

                    <Route path="/" element={<ProtectedRoute component={<><Dashboard /></>} />} />
                    <Route path="/dashboard/courses/:courseId" element={<ProtectedRoute page='dashboard' component={<><CompanyCourseDashboard /></>} />} />
                    <Route path="/developers" element={<ProtectedRoute component={<><Developers /></>} />} />
                    <Route path="/developers/keys/:publicKey" element={<ProtectedRoute component={<><DeveloperKeyDetails /></>} />} />
                    <Route path="/developers/documentation" element={<ProtectedRoute component={<><DevelopersDocumentation /></>} />} />
                    <Route path="/careers" element={<ProtectedRoute component={<><Careers /></>} />} />
                    <Route path="/careers/:careerId" element={<ProtectedRoute component={<><Career /></>} />} />
                    <Route path="/courses" element={<ProtectedRoute component={<><Courses /></>} />} />
                    <Route path="/courses/:courseId" element={<ProtectedRoute component={<><Course /></>} />} />
                    <Route path="/courses/:courseId/editions/:editionId" element={<ProtectedRoute component={<><EditionEditor /></>} />} />
                    <Route path="/courses/:courseId/editions/:editionId/modules/:moduleId" element={<ProtectedRoute component={<><ModuleEditor /></>} />} />
                    <Route path="/users" element={<ProtectedRoute component={<><Users defaultSelectedTab={UserPages.Students} /></>} />} />
                    <Route path="/users/students" element={<ProtectedRoute component={<><Users defaultSelectedTab={UserPages.Students} /></>} />} />
                    <Route path="/users/company/students/:studentId" element={<ProtectedRoute component={<><CompanyUser /></>} />} />
                    <Route path="/users/students/:studentId" element={<ProtectedRoute component={<><Student /></>} />} />
                    <Route path="/users/teachers" element={<ProtectedRoute component={<><Users defaultSelectedTab={UserPages.Teachers} /></>} />} />
                    <Route path="/users/teachers/:teacherId" element={<ProtectedRoute component={<><Teacher /></>} />} />
                    <Route path="/users/tutors" element={<ProtectedRoute component={<><Users defaultSelectedTab={UserPages.Tutors} /></>} />} />
                    <Route path="/users/tutors/:tutorId" element={<ProtectedRoute component={<><Teacher role={Role.Tutor} /></>} />} />
                    <Route path="/users/admins" element={<ProtectedRoute component={<><Users defaultSelectedTab={UserPages.Admins} /></>} />} />
                    <Route path="/users/companies" element={<ProtectedRoute component={<><Users defaultSelectedTab={UserPages.Companies} /></>} />} />
                    <Route path="/users/companies/:companyId" element={<ProtectedRoute component={<><Company /></>} />} />
                    <Route path="/users/admins/:adminId" element={<ProtectedRoute component={<><Admin /></>} />} />
                    <Route path="/notifications" element={<ProtectedRoute component={SHOW_NOTIFICATIONS ? <Notifications /> : <Navigate to="/" replace />} />} />
                    <Route path="/messages/" element={<ProtectedRoute component={<><Chats /></>} />} />
                    <Route path="/macros/" element={<ProtectedRoute component={<><Macros /></>} />} />
                    <Route path="/macros/:macroId" element={<ProtectedRoute component={<><MacroEditor /></>} />} />
                    <Route path="/macros/:macroId/preview" element={<ProtectedRoute component={<><MacroPreview /></>} />} />
                    <Route path="/macroPreview/:previewId" element={<ProtectedRoute component={<><MacroPreview /></>} />} />
                    <Route path="/settings" element={<ProtectedRoute component={<><Settings /></>} />} />
                    <Route path="/profile" element={<ProtectedRoute component={<><Profile /></>} />} />
                    <Route path="/changePassword" element={<ProtectedRoute component={<><ChangePassword /></>} />} />
                    <Route path="/orders" element={<ProtectedRoute component={<><Orders /></>} />} />
                    <Route path="/orders/:orderId" element={<ProtectedRoute component={<><Order /></>} />} />
                    <Route path="/invoices" element={<ProtectedRoute component={<><InvoiceRequests /></>} />} />
                    <Route path="/videos" element={<ProtectedRoute component={<><Videos /></>} />} />
                    <Route path="/videos/:video_id" element={<ProtectedRoute component={<><Video /></>} />} />
                    <Route path="/videos/:video_id/editor" element={<ProtectedRoute component={<><VideoEditor /></>} />} />
                    <Route path="/downloads" element={<ProtectedRoute component={<><Downloads /></>} />} />
                    <Route path="/downloads/:download_id/editor" element={<ProtectedRoute component={<><DownloadEditor /></>} />} />
                    <Route path="/lessons/:lesson_id/recordings/editor" element={<ProtectedRoute component={<><RecordingsEditor /></>} />} />
                    {/* <Route path="/bookings" element={<ProtectedRoute component={<><Bookings /></>} permissionLevel={[...PermissionLevel.Sales, ...PermissionLevel.Marketing]} />} /> */}
                    <Route path="/reports/teaching" element={<ProtectedRoute component={<><TeachingReports /></>} />} />
                    <Route path="/reports/teaching/courses/:courseId" element={<ProtectedRoute component={<><CourseReports /></>} />} />
                    <Route path="/exercises" element={<ProtectedRoute component={<><Exercises /></>} />} />
                    <Route path="/exercises/:exerciseId" element={<ProtectedRoute component={<><Exercise /></>} />} />
                    <Route path="/materials" element={<ProtectedRoute component={<><Materials /></>} />} />
                    <Route path="/tests" element={<ProtectedRoute component={<><Tests /></>} />} />
                    <Route path="/tests/:testId" element={<ProtectedRoute component={<><TestResult /></>} />} />
                    <Route path="/shop_courses" element={<ProtectedRoute component={<><ShopCourses /></>} />} />
                    <Route path="/shop_courses/:courseId/editor" element={<ProtectedRoute component={<><CourseEditor /></>} />} />
                    <Route path="/shop_courses/:courseId/preview" element={<ProtectedRoute component={<><CoursePreview /></>} />} />
                    <Route path="/articles" element={<ProtectedRoute component={<><Articles /></>} />} />
                    <Route path="/articles/:article_id" element={<ProtectedRoute component={<><ArticleEditor /></>} />} />
                    <Route path="/reviews" element={<ProtectedRoute component={<><Reviews /></>} />} />
                    <Route path="/reviews/:review_id" element={<ProtectedRoute component={<><ReviewEditor /></>} />} />
                    <Route path="/course-quiz-campaigns/*" element={<LegacyCampaignRedirect />} />
                    <Route path="/campaigns" element={<ProtectedRoute component={<CourseQuizCampaigns />} />} />
                    <Route path="/campaigns/:campaignId" element={<ProtectedRoute component={<CourseQuizCampaignEditor />} />} />
                    <Route path="/surveys" element={<ProtectedRoute component={<><Surveys /></>} />} />
                    <Route path="/surveys/:survey_id" element={<ProtectedRoute component={<><Survey /></>} />} />
                    <Route path="/surveys/:survey_id/editor" element={<ProtectedRoute component={<><SurveyEditor /></>} />} />
                    <Route path="/surveys/:survey_id/students/:students_id" element={<ProtectedRoute component={<><StudentSurvey /></>} />} />
                    <Route path="/lessons/:roomName" element={<ProtectedRoute component={<><LiveLesson /></>} />} />
                    <Route path="/applications" element={<ProtectedRoute page="applications" component={<><ApplicationsV2 /></>} />} />
                    <Route path="/applications-legacy" element={<ProtectedRoute page="applications" component={<><Applications /></>} />} />
                    <Route path="/applications-v2" element={<Navigate to="/applications" replace />} />
                    <Route path="/communications" element={<ProtectedRoute component={<><Communications /></>} />} />
                    <Route path="/feedbacks" element={<ProtectedRoute component={<><Feedbacks /></>} />} />
                    <Route path="/coupons" element={<ProtectedRoute component={<><Coupons /></>} />} />
                    <Route path="/reports/sales" element={<ProtectedRoute component={<><Reports /></>} />} />
                    <Route path="/reports/orders" element={<ProtectedRoute component={<><OrdersReports /></>} />} />
                    <Route path="/reports/marketing" element={<ProtectedRoute component={<><MarketingReports /></>} />} />
                    <Route path='/forms' element={<ProtectedRoute component={<Forms />} />} />
                    <Route path='/mailings' element={<ProtectedRoute component={<><Mailings /></>} />} />
                    <Route path='/lessons' element={<ProtectedRoute component={<><Lessons /></>} />} />
                    <Route path='/events' element={<ProtectedRoute component={<><Events /></>} />} />
                    <Route path='/events/:eventId' element={<ProtectedRoute component={<><Event /></>} />} />
                    <Route path='/events/:id/manager' element={<ProtectedRoute component={<><EventManager /></>} />} />
                    <Route path="/events/:eventId/editor" element={<ProtectedRoute component={<><EventEditor /></>} />} />
                    <Route path="/events/:eventId/preview" element={<ProtectedRoute component={<><EventPreview /></>} />} />
                    <Route path="/articles" element={<ProtectedRoute component={<><Articles /></>} />} />
                    <Route path="/articles/:articleId/editor" element={<ProtectedRoute component={<><ArticleEditor /></>} />} />
                    <Route path="/articles/:articleId/preview" element={<ProtectedRoute component={<><ArticlePreview /></>} />} />
                    <Route path='/jobs' element={<ProtectedRoute component={<><RecordingsJobs /></>} />} />

                  </Routes>
                </div>
              </ErrorBoundary>
            </WebSocketProvider>
          </div>
        </ThemeProvider>
      </MainContext.Provider>
    </Router>
  );
}

export default App;
